import { GlitchEffect } from './effects/GlitchEffect.js';
import { CRTEffect } from './effects/CRTEffect.js';
import { FilmGrainEffect } from './effects/FilmGrainEffect.js';
import { ScanlineEffect } from './effects/ScanlineEffect.js';
import { PhosphorEffect } from './effects/PhosphorEffect.js';
import { UniverseEffect } from './effects/UniverseEffect.js';
import { MatrixEffect } from './effects/MatrixEffect.js';
import { OutrunEffect } from './effects/OutrunEffect.js';
import { GodRaysEffect } from './effects/GodRaysEffect.js';
import { DissolveEffect } from './effects/DissolveEffect.js';
import { FogEffect } from './effects/FogEffect.js';
import { HeatDistortionEffect } from './effects/HeatDistortionEffect.js';
import { NeonBloomEffect } from './effects/NeonBloomEffect.js';
import { HalftoneEffect } from './effects/HalftoneEffect.js';
import { MotionBlurEffect } from './effects/MotionBlurEffect.js';
import { ChromaticEffect } from './effects/ChromaticEffect.js';
import { LensDistortionEffect } from './effects/LensDistortionEffect.js';
import { WaveDistortionEffect } from './effects/WaveDistortionEffect.js';
import { WaterRippleEffect } from './effects/WaterRippleEffect.js';
import { SyncFailureEffect } from './effects/SyncFailureEffect.js';
import { ProteanCloudsEffect } from './effects/ProteanCloudsEffect.js';
import { DriveHomeEffect } from './effects/DriveHomeEffect.js';
import { SpiralEffect } from './effects/SpiralEffect.js';
import { CanyonEffect } from './effects/CanyonEffect.js';
import { eventBus } from '../utils/EventEmitter.js';

/**
 * Manages multiple shader effects
 */
export class EffectsManager {
    constructor(shaderManager) {
        this.shaderManager = shaderManager;
        this.effects = new Map();
        this.activeEffect = null;
        this.startTime = performance.now();
        this.setupEffects();
    }

    /**
     * Set up available effects
     */
    async setupEffects() {
        // Create effects
        const glitchEffect = new GlitchEffect(this.shaderManager);
        const crtEffect = new CRTEffect(this.shaderManager);
        const filmGrainEffect = new FilmGrainEffect(this.shaderManager);
        const scanlineEffect = new ScanlineEffect(this.shaderManager);
        const phosphorEffect = new PhosphorEffect(this.shaderManager);
        const universeEffect = new UniverseEffect(this.shaderManager);
        const matrixEffect = new MatrixEffect(this.shaderManager);
        const outrunEffect = new OutrunEffect(this.shaderManager);
        const godRaysEffect = new GodRaysEffect(this.shaderManager);
        const dissolveEffect = new DissolveEffect(this.shaderManager);
        const fogEffect = new FogEffect(this.shaderManager);
        const heatDistortionEffect = new HeatDistortionEffect(this.shaderManager);
        const neonBloomEffect = new NeonBloomEffect(this.shaderManager);
        const halftoneEffect = new HalftoneEffect(this.shaderManager);
        const motionBlurEffect = new MotionBlurEffect(this.shaderManager);
        const chromaticEffect = new ChromaticEffect(this.shaderManager);
        const lensDistortionEffect = new LensDistortionEffect(this.shaderManager);
        const waveDistortionEffect = new WaveDistortionEffect(this.shaderManager);
        const waterRippleEffect = new WaterRippleEffect(this.shaderManager);
        const syncFailureEffect = new SyncFailureEffect(this.shaderManager);
        const proteanCloudsEffect = new ProteanCloudsEffect(this.shaderManager);
        const driveHomeEffect = new DriveHomeEffect(this.shaderManager);
        const spiralEffect = new SpiralEffect(this.shaderManager);
        const canyonEffect = new CanyonEffect(this.shaderManager);

        // Add effects to map
        this.effects.set('glitch', glitchEffect);
        this.effects.set('crt', crtEffect);
        this.effects.set('film-grain', filmGrainEffect);
        this.effects.set('scanlines', scanlineEffect);
        this.effects.set('phosphor', phosphorEffect);
        this.effects.set('universe', universeEffect);
        this.effects.set('matrix', matrixEffect);
        this.effects.set('outrun', outrunEffect);
        this.effects.set('god-rays', godRaysEffect);
        this.effects.set('pixel-dissolve', dissolveEffect);
        this.effects.set('fog', fogEffect);
        this.effects.set('heat-distortion', heatDistortionEffect);
        this.effects.set('neon', neonBloomEffect);
        this.effects.set('halftone', halftoneEffect);
        this.effects.set('motion-blur', motionBlurEffect);
        this.effects.set('chromatic-aberration', chromaticEffect);
        this.effects.set('lens-distortion', lensDistortionEffect);
        this.effects.set('wave-distortion', waveDistortionEffect);
        this.effects.set('water-ripples', waterRippleEffect);
        this.effects.set('sync-failure', syncFailureEffect);
        this.effects.set('protean-clouds', proteanCloudsEffect);
        this.effects.set('drive-home', driveHomeEffect);
        this.effects.set('spiral', spiralEffect);
        this.effects.set('canyon', canyonEffect);

        // Initialize effects
        await Promise.all([
            glitchEffect.init(),
            crtEffect.init(),
            filmGrainEffect.init(),
            scanlineEffect.init(),
            phosphorEffect.init(),
            universeEffect.init(),
            matrixEffect.init(),
            outrunEffect.init(),
            godRaysEffect.init(),
            dissolveEffect.init(),
            fogEffect.init(),
            heatDistortionEffect.init(),
            neonBloomEffect.init(),
            halftoneEffect.init(),
            motionBlurEffect.init(),
            chromaticEffect.init(),
            lensDistortionEffect.init(),
            waveDistortionEffect.init(),
            waterRippleEffect.init(),
            syncFailureEffect.init(),
            proteanCloudsEffect.init(),
            driveHomeEffect.init(),
            spiralEffect.init(),
            canyonEffect.init()
        ]);

        // Set active effect from localStorage or default to glitch
        const lastEffect = localStorage.getItem('lastEffect') || 'glitch';
        this.setActiveEffect(lastEffect);
    }

    /**
     * Save effect configuration to localStorage
     * @param {string} effectName - Name of the effect
     * @param {Object} params - Effect parameters
     */
    saveEffectConfig(effectName, params) {
        try {
            const configs = JSON.parse(localStorage.getItem('effectConfigs')) || {};
            configs[effectName] = params;
            localStorage.setItem('effectConfigs', JSON.stringify(configs));
        } catch (error) {
            console.warn('Failed to save effect config:', error);
        }
    }

    /**
     * Load effect configuration from localStorage
     * @param {string} effectName - Name of the effect
     * @returns {Object|null} Effect parameters or null if not found
     */
    loadEffectConfig(effectName) {
        try {
            const configs = JSON.parse(localStorage.getItem('effectConfigs')) || {};
            return configs[effectName] || null;
        } catch (error) {
            console.warn('Failed to load effect config:', error);
            return null;
        }
    }

    /**
     * Set the active effect
     * @param {string} name - Effect name
     */
    setActiveEffect(name) {
        const effect = this.effects.get(name);
        if (effect) {
            if (this.activeEffect !== effect) {
                // Load saved configuration if available
                const savedConfig = this.loadEffectConfig(name);
                if (savedConfig) {
                    effect.updateParams(savedConfig);
                }
                
                this.activeEffect = effect;
                localStorage.setItem('lastEffect', name);
                eventBus.emit('effect:activated', { name });
            }
        } else {
            console.warn(`Effect ${name} not found`);
        }
    }

    /**
     * Update effect parameters
     * @param {string} name - Effect name
     * @param {Object} params - New parameters
     */
    updateEffectParams(name, params) {
        const effect = this.effects.get(name);
        if (effect) {
            effect.updateParams(params);
            // Save the updated configuration
            this.saveEffectConfig(name, effect.getParams());
        }
    }

    /**
     * Apply the active effect
     * @param {number} time - Current time in seconds
     * @param {Object} resolution - Canvas resolution {width, height}
     */
    applyEffect(time, resolution) {
        if (this.activeEffect) {
            const program = this.activeEffect.apply(time, resolution);
            return program;
        }
    }

    /**
     * Get an effect by name
     * @param {string} name - Effect name
     * @returns {BaseEffect} The effect
     */
    getEffect(name) {
        return this.effects.get(name);
    }

    /**
     * Get the active effect
     * @returns {BaseEffect} The active effect
     */
    getActiveEffect() {
        return this.activeEffect;
    }

    /**
     * Get all available effects
     * @returns {Map} Map of effects
     */
    getEffects() {
        return new Map(this.effects);
    }

    /**
     * Get UI configurations for all effects
     * @returns {Object} UI configurations
     */
    getUIConfigs() {
        const configs = {};
        this.effects.forEach((effect, name) => {
            configs[name] = effect.getUIConfig();
        });
        return configs;
    }

    /**
     * Check if an effect is implemented
     * @param {string} name - Effect name
     * @returns {boolean} Whether the effect is implemented
     */
    hasEffect(name) {
        return this.effects.has(name);
    }

    /**
     * Get all implemented effects
     * @returns {string[]} Array of implemented effect names
     */
    getImplementedEffects() {
        return Array.from(this.effects.keys());
    }
} 